import { useEffect, useMemo, useRef, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Check, ChevronDown, Plus, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useLanguage } from '@/context/LanguageContext'
import {
  ConditionsValue,
  FabricGroupRule,
  isFabricRule,
} from './componentConditions'

// While editing keep half-built parts (empty rule / group without codes); the
// form runs normalizeConditions() on submit. Only a fully empty object -> null.
const rawOrNull = (v: Record<string, any>): ConditionsValue =>
  Object.keys(v).length ? v : null

const GET_CONDITION_COMPONENT_GROUPS = gql`
  query GET_CONDITION_COMPONENT_GROUPS(
    $where: AdditionalComponentGroupWhereInput
  ) {
    findManyAdditionalComponentGroup(where: $where) {
      id
      code
      manufacturerId
      additional_component_group_profiles {
        name
        language
      }
      additional_components {
        id
        code
        additional_component_profiles {
          name
          language
        }
      }
    }
  }
`

const SEARCH_CONDITION_FABRIC_GROUPS = gql`
  query SEARCH_CONDITION_FABRIC_GROUPS(
    $where: FabricGroupWhereInput
    $take: Int
  ) {
    findManyFabricGroup(
      where: $where
      take: $take
      orderBy: [{ code: { sort: asc } }]
    ) {
      id
      code
      fabric_group_profiles {
        name
        language
      }
    }
  }
`

const pickName = (profiles: any[] | undefined, language: string) =>
  profiles?.find(p => p.language?.toLowerCase() === language?.toLowerCase())
    ?.name ||
  profiles?.[0]?.name ||
  ''

/** Searchable popover that adds one fabric group code. */
function FabricGroupAdder({
  manufacturerId,
  selected,
  language,
  onAdd,
}: {
  manufacturerId?: number
  selected: string[]
  language: string
  onAdd: (code: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [term, setTerm] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setTerm(input), 250)
    return () => clearTimeout(t)
  }, [input])

  const { data, loading } = useQuery(SEARCH_CONDITION_FABRIC_GROUPS, {
    variables: {
      take: 50,
      where: {
        ...(manufacturerId
          ? { manufacturerId: { equals: manufacturerId } }
          : {}),
        OR: [
          { code: { mode: 'insensitive', contains: term } },
          {
            fabric_group_profiles: {
              some: { name: { mode: 'insensitive', contains: term } },
            },
          },
        ],
      },
    },
    skip: !open,
    fetchPolicy: 'network-only',
  })
  const groups: any[] = data?.findManyFabricGroup ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Plus className="mr-1 h-4 w-4" />
          Add fabric group
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by code or name..."
            value={input}
            onValueChange={setInput}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Searching...' : 'No fabric groups found.'}
            </CommandEmpty>
            {groups.map(g => {
              const added = selected.includes(g.code)
              return (
                <CommandItem
                  key={g.id}
                  value={String(g.id)}
                  disabled={added}
                  onSelect={() => {
                    if (added) return
                    onAdd(g.code)
                    setOpen(false)
                    setInput('')
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${added ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="font-mono text-sm mr-2">{g.code}</span>
                  <span className="truncate">
                    {pickName(g.fabric_group_profiles, language)}
                  </span>
                </CommandItem>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** Chip list + adder for one fabricGroup rule. */
function FabricRuleRow({
  rule,
  manufacturerId,
  language,
  onChange,
  onRemove,
}: {
  rule: FabricGroupRule
  manufacturerId?: number
  language: string
  onChange: (next: FabricGroupRule) => void
  onRemove: () => void
}) {
  // ERP rules are read-only here: the import owns them and rewrites them nightly.
  const readOnly = rule.source === 'erp'

  const { data } = useQuery(SEARCH_CONDITION_FABRIC_GROUPS, {
    variables: {
      take: Math.max(rule.codes.length, 1),
      where: {
        ...(manufacturerId
          ? { manufacturerId: { equals: manufacturerId } }
          : {}),
        code: { in: rule.codes },
      },
    },
    skip: rule.codes.length === 0,
  })
  const byCode = useMemo(() => {
    const m = new Map<string, string>()
    for (const g of data?.findManyFabricGroup ?? [])
      m.set(g.code, pickName(g.fabric_group_profiles, language))
    return m
  }, [data, language])

  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex max-w-full flex-wrap rounded-md border overflow-hidden">
          {(['in', 'notIn'] as const).map(op => (
            <button
              key={op}
              type="button"
              disabled={readOnly}
              onClick={() => onChange({ ...rule, op })}
              className={`px-3 py-1 text-xs ${
                rule.op === op
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-accent'
              } disabled:opacity-60`}
            >
              {op === 'in'
                ? 'Allow only these fabrics'
                : 'Exclude these fabrics'}
            </button>
          ))}
        </div>
        {readOnly && <Badge variant="lightBlue">from ERP</Badge>}
        {!readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 ml-auto shrink-0"
            onClick={onRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 pt-1">
        {rule.codes.map(code => {
          const name = byCode.get(code)
          const known = !!name || byCode.has(code)
          return (
            <Badge
              key={code}
              variant={known || !data ? 'secondary' : 'destructive'}
              className="gap-1"
            >
              <span className="font-mono">{code}</span>
              {name && <span className="font-normal">{name}</span>}
              {data && !known && <span className="font-normal">unknown</span>}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...rule,
                      codes: rule.codes.filter(c => c !== code),
                    })
                  }
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          )
        })}
        {!readOnly && (
          <FabricGroupAdder
            manufacturerId={manufacturerId}
            selected={rule.codes}
            language={language}
            onAdd={code => onChange({ ...rule, codes: [...rule.codes, code] })}
          />
        )}
      </div>
      {readOnly && (
        <p className="text-xs text-muted-foreground">
          Managed by the ERP import and overwritten on the next run. Add a
          manual rule instead of editing this one.
        </p>
      )}
    </div>
  )
}

interface ComponentConditionsEditorProps {
  value: ConditionsValue
  onChange: (value: ConditionsValue) => void
  manufacturerId?: number
  /** Optional hint rendered under the editor. */
  hint?: string
  /** Server-side validation message (BAD_USER_INPUT). */
  error?: string | null
}

export function ComponentConditionsEditor({
  value,
  onChange,
  manufacturerId,
  hint,
  error,
}: ComponentConditionsEditorProps) {
  const { language } = useLanguage()
  const current: Record<string, any> = value ?? {}
  const rules: any[] = Array.isArray(current.rules) ? current.rules : []
  const comps: string[] = Array.isArray(current.onlyWithComponents)
    ? current.onlyWithComponents
    : []
  const groupCode: string = current.onlyWithComponentsGroup ?? ''

  const emit = (patch: Record<string, any>) =>
    onChange(rawOrNull({ ...current, ...patch }))

  const { data: groupsData } = useQuery(GET_CONDITION_COMPONENT_GROUPS, {
    variables: {
      where: manufacturerId
        ? { manufacturerId: { equals: manufacturerId } }
        : undefined,
    },
  })
  const groups: any[] = groupsData?.findManyAdditionalComponentGroup ?? []
  // Legacy rows may reference a group outside the loaded list: keep it selectable.
  const groupCodes = Array.from(
    new Set([
      ...groups.map(g => g.code as string),
      ...(groupCode ? [groupCode] : []),
    ])
  ).sort()
  const groupNameByCode = new Map<string, string>(
    groups.map(g => [
      g.code as string,
      pickName(g.additional_component_group_profiles, language),
    ])
  )
  const [groupOpen, setGroupOpen] = useState(false)
  const selectGroup = (code: string) => {
    setGroupOpen(false)
    if (code === groupCode) return
    emit({ onlyWithComponentsGroup: code, onlyWithComponents: [] })
  }
  const groupComponents: any[] = groups
    .filter(g => g.code === groupCode)
    .flatMap(g => g.additional_components ?? [])
  const componentCodes = Array.from(
    new Set([...groupComponents.map(c => c.code as string), ...comps])
  )
  const nameByCode = new Map<string, string>(
    groupComponents.map(c => [
      c.code,
      pickName(c.additional_component_profiles, language),
    ])
  )

  // Advanced JSON: local text so half-typed JSON does not fight the controlled value.
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(value ?? {}, null, 2)
  )
  const [jsonError, setJsonError] = useState<string | null>(null)
  const lastEmitted = useRef<string>(JSON.stringify(value ?? null))
  useEffect(() => {
    const incoming = JSON.stringify(value ?? null)
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming
      setJsonText(JSON.stringify(value ?? {}, null, 2))
      setJsonError(null)
    }
  }, [value])

  const emitTracked = (next: ConditionsValue) => {
    lastEmitted.current = JSON.stringify(next ?? null)
    onChange(next)
  }

  const updateRule = (index: number, next: any) =>
    emit({ rules: rules.map((r, i) => (i === index ? next : r)) })

  return (
    <div className="space-y-3 col-span-3 min-w-0">
      <div className="rounded-lg border p-3 space-y-3">
        <div className="space-y-1">
          <Label className="text-xs uppercase text-muted-foreground">
            Only with components
          </Label>
          <p className="text-xs text-muted-foreground">
            Shown only when the selected component in the group is one of these.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Popover open={groupOpen} onOpenChange={setGroupOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 max-w-full justify-between"
              >
                <span className="truncate">
                  {groupCode ? (
                    <>
                      <span className="font-mono">{groupCode}</span>
                      {groupNameByCode.get(groupCode) && (
                        <span className="ml-2 font-normal">
                          {groupNameByCode.get(groupCode)}
                        </span>
                      )}
                    </>
                  ) : (
                    'No restriction'
                  )}
                </span>
                <ChevronDown className="ml-1 h-4 w-4 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search groups..." />
                <CommandList>
                  <CommandEmpty>No groups found.</CommandEmpty>
                  <CommandItem
                    value="no restriction"
                    onSelect={() => selectGroup('')}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${!groupCode ? 'opacity-100' : 'opacity-0'}`}
                    />
                    No restriction
                  </CommandItem>
                  {groupCodes.map(code => (
                    <CommandItem
                      key={code}
                      value={`${code} ${groupNameByCode.get(code) ?? ''}`}
                      onSelect={() => selectGroup(code)}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${code === groupCode ? 'opacity-100' : 'opacity-0'}`}
                      />
                      <span className="font-mono text-sm mr-2">{code}</span>
                      <span className="truncate">
                        {groupNameByCode.get(code)}
                      </span>
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {groupCode && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9"
                >
                  Components
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search components..." />
                  <CommandList>
                    <CommandEmpty>No components.</CommandEmpty>
                    {componentCodes.map(code => {
                      const on = comps.includes(code)
                      return (
                        <CommandItem
                          key={code}
                          value={`${code} ${nameByCode.get(code) ?? ''}`}
                          onSelect={() =>
                            emit({
                              onlyWithComponents: on
                                ? comps.filter(c => c !== code)
                                : [...comps, code],
                            })
                          }
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${on ? 'opacity-100' : 'opacity-0'}`}
                          />
                          <span className="font-mono text-sm mr-2">{code}</span>
                          <span className="truncate">
                            {nameByCode.get(code)}
                          </span>
                        </CommandItem>
                      )
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
        {comps.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {comps.map(code => (
              <Badge key={code} variant="secondary" className="gap-1">
                <span className="font-mono">{code}</span>
                {nameByCode.get(code) && (
                  <span className="font-normal">{nameByCode.get(code)}</span>
                )}
                <button
                  type="button"
                  onClick={() =>
                    emit({ onlyWithComponents: comps.filter(c => c !== code) })
                  }
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border p-3 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <Label className="text-xs uppercase text-muted-foreground">
              Fabric restriction
            </Label>
            <p className="text-xs text-muted-foreground">
              With combination fabrics the rule applies to every material. Rules
              with no groups are dropped on save.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() =>
              emit({
                rules: [
                  ...rules,
                  {
                    type: 'fabricGroup',
                    op: 'in',
                    codes: [],
                    source: 'manual',
                  },
                ],
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            Add fabric rule
          </Button>
        </div>
        {rules.length === 0 && (
          <p className="text-xs text-muted-foreground">No fabric rules</p>
        )}
        {rules.map((rule, i) =>
          isFabricRule(rule) ? (
            <FabricRuleRow
              key={i}
              rule={rule}
              manufacturerId={manufacturerId}
              language={language}
              onChange={next => updateRule(i, next)}
              onRemove={() => emit({ rules: rules.filter((_, j) => j !== i) })}
            />
          ) : (
            <div
              key={i}
              className="text-xs text-muted-foreground border rounded-md p-2"
            >
              Rule of type "{String(rule?.type)}" - edit in Advanced JSON.
            </div>
          )
        )}
      </div>

      <div className="rounded-lg border p-3 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-xs uppercase text-muted-foreground">
            Advanced JSON
          </Label>
          <button
            type="button"
            className="text-xs text-muted-foreground underline"
            onClick={() => setAdvancedOpen(o => !o)}
          >
            {advancedOpen ? 'Hide' : 'Show'} advanced JSON
          </button>
        </div>
        {advancedOpen && (
          <div className="space-y-1">
            <Textarea
              className="md:text-xs font-mono min-h-[140px]"
              value={jsonText}
              onChange={e => {
                const text = e.target.value
                setJsonText(text)
                try {
                  const parsed = text.trim() ? JSON.parse(text) : {}
                  if (
                    parsed === null ||
                    typeof parsed !== 'object' ||
                    Array.isArray(parsed)
                  ) {
                    setJsonError('Conditions must be a JSON object')
                    return
                  }
                  setJsonError(null)
                  emitTracked(rawOrNull(parsed))
                } catch (err) {
                  setJsonError((err as Error).message)
                }
              }}
            />
            {jsonError && (
              <p className="text-xs text-destructive">
                Invalid JSON (not applied): {jsonError}
              </p>
            )}
          </div>
        )}
      </div>

      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
